import React, { useEffect, useState, useMemo } from 'react';
import { Box } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import { Stop } from '@app/shared/models/tour.type';
import { MapCard } from '@app/new-app/MapCard';
import { LockedStopOverlay } from './LockedStopOverlay';
import { calculateDemoStopLimit } from './utils';
import { atom_tourState } from '@app/shared/state/tour.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_modalType } from '@app/shared/state/show-content-modal.atom';
import { selector_visibleStopIds } from '@app/shared/state/filter.atom';
import { useRenderTracker } from '@app/shared/debug';

interface MobileSwiperContentProps {
  isHebrew: boolean;
  isListMode: boolean;

}

export const MobileSwiperContent: React.FC<MobileSwiperContentProps> = ({
  isHebrew,
  isListMode,
}) => {
  useRenderTracker('MobileSwiperContent');
  const { t } = useTranslation();
  const [swiperInstance, setSwiperInstance] = useState<any>(null);

  // Import atoms directly
  const tourState = useRecoilValue(atom_tourState);
  const [activeStopState, setActiveStopState] = useRecoilState(atom_activeStopState);
  const setModalType = useSetRecoilState(atom_modalType);
  const visibleStopIds = useRecoilValue(selector_visibleStopIds);
  const stops = tourState?.stops || [];
  const activeStopId = activeStopState?.id;
  const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
  const isDemo = uID?.toLowerCase() === 'demo';
  const demoStopLimit = useMemo(
    () => calculateDemoStopLimit(isDemo, tourState?.config?.demo_stop),
    [isDemo, tourState?.config?.demo_stop]
  );

  const handleSwipe = (index: number) => {
    if (displayStops?.[index]) {
      setActiveStopState(displayStops[index]);
    }
  };

  // Handle swiper initialization
  const handleSwiperInit = (swiper: any) => {
    setSwiperInstance(swiper);
  };

  // Auto-slide to selected stop when activeStopId changes
  useEffect(() => {
    if (!activeStopId) return;
    if (!swiperInstance) return;
    if (typeof swiperInstance.slideTo !== 'function') return;

    const selectedIndex = displayStops.findIndex((stop: Stop) => stop.id === activeStopId);

    if (typeof selectedIndex === 'number' && selectedIndex >= 0 && swiperInstance.activeIndex !== selectedIndex) {
      swiperInstance.slideTo(selectedIndex, 300);
    }
  }, [activeStopId, swiperInstance, stops]);

  // Filter stops by visibility (bookmarks/filters)
  const displayStops = useMemo(() =>
    stops.filter(stop => visibleStopIds.has(stop.id) && (!!stop.stop_index || !!stop.show_on_slider)),
    [stops, visibleStopIds]
  );

  return (
    <div
      key={isHebrew + 'test'}
      style={{ direction: isHebrew ? 'rtl' : 'ltr' }}
    >
      <Box
        zIndex={500}
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        className="w-full pointer-events-auto"
        sx={{
          alignItems: !isHebrew ? 'flex-start' : 'flex-end',
          zIndex: 500,
          position: 'absolute',
          bottom: '60px',
          width: '100%',
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            left: isHebrew ? undefined : 0,
            right: isHebrew ? 0 : undefined,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1001,
            pointerEvents: 'auto',
            background: 'none',
            paddingTop: 0,
            paddingBottom: 0,
          }}
        >
        </div>
        <Swiper
          spaceBetween={14}
          slidesPerView={1.05}
          touchMoveStopPropagation
          onSwiper={handleSwiperInit}
          onSlideChange={swiper => handleSwipe(swiper.activeIndex)}
          style={{
            overflow: 'visible',
            margin: '0 4px',
            paddingTop: 0,
          }}
        >
          {displayStops
            .map((stop, idx) => {
              const isLocked = isDemo && (demoStopLimit === -1 ? true : (typeof demoStopLimit === 'number' ? idx > demoStopLimit : false));

              return (
                <SwiperSlide
                  style={{
                    direction: isHebrew ? 'rtl' : 'ltr',
                    alignSelf: 'flex-end',
                    position: isLocked ? 'relative' : undefined
                  }}
                  key={'stopstop' + stop.id}
                >
                  <motion.div
                    whileTap={{ scale: 0.9 }}
                    dragElastic
                  >
                    <div style={{
                      margin: '0px 2px',
                      paddingBottom: '10px',
                      filter: isLocked ? 'blur(4px)' : 'none',
                      pointerEvents: isLocked ? 'none' : 'auto'
                    }}>
                      <MapCard
                        key={stop.id}
                        title={stop.s_title}
                        description={stop.s_smallDesc}
                        activeStop={stop}
                        image={stop.loc_pics?.[0]?.url?.split('&token=')?.[0]}
                        categories={stop?.tags?.map(tag => tag.text)}
                        rating={stop.rating}
                        onSelect={() => {
                          if (activeStopId === stop.id) {
                            return;
                          }
                          // onStopSelect(stop);
                          // flyTo([stop.stop_location?.latitude, stop.stop_location?.longitude], 18);
                        }}
                        isSelected={stop.id === activeStopId}
                        isListMode={isListMode}
                      />
                    </div>
                    {isLocked && (
                      <LockedStopOverlay
                        isHebrew={isHebrew}
                        onPurchaseClick={() => setModalType('purchaseModalFromDemo')}
                      />
                    )}
                  </motion.div>
                </SwiperSlide>
              );
            })}
        </Swiper>
      </Box>
    </div>
  );
};
